import { css } from '@mui/material/styles';
const cssReset = css `
    &.MuiInput-root,
    &.MuiOutlinedInput-root,
    &.MuiFilledInput-root,
    .MuiAutocomplete-inputRoot {
        all: unset;
        box-sizing: border-box;
    }

    .MuiInputBase-input,
    .MuiNativeSelect-select,
    .MuiSelect-select {
        all: unset;
        min-width: auto;
        width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;

        &:focus {
            background: unset;
        }

        &::placeholder {
            opacity: unset;
        }
    }

    &::before,
    &::after {
        all: unset;
        display: none;
    }
`;
const isSmallInput = `
    &[data-fuel-size=sm],
    &[data-fuel-size=small],
    &.MuiInputBase-sizeSmall,
    &.MuiInputBase-sizeSm
`;
const isMediumInput = `
    &,
    &[data-fuel-size=md],
    &[data-fuel-size=medium],
    &.MuiInputBase-sizeMedium,
    &.MuiInputBase-sizeMd
`;
const isLargeInput = `
    &[data-fuel-size=lg],
    &[data-fuel-size=large],
    &.MuiInputBase-sizeLarge,
    &.MuiInputBase-sizeLg
`;
export const inputComponentOverrides = {
    // elevated input is Mui standard
    MuiInputBase: {
        styleOverrides: {
            root: ({ theme }) => css `
                ${cssReset}

                &.MuiInputBase-root.MuiInputBase-root {
                    --input-font-size: ${theme.tokens.FuelFontSizeBase};
                    --input-line-height: ${theme.tokens.FuelLineHeightBase};
                    --input-background-color: ${theme.tokens.FuelColorWhite};
                    --input-height: 36px;
                    --input-spacing-x: 0.5rem;
                    --input-spacing-y: 0.375rem;
                    --input-icon-size: 24px;
                    --input-icon-box-size: calc(var(--input-height) - var(--input-spacing-y) * 2)

                    --input-border-color: transparent;
                    --input-border-thickness: 1px;

                    display: inline-flex;
                    flex-direction: row;
                    position: relative;
                    cursor: unset;

                    gap: 0.5rem;
                    border-radius: 0.25rem;
                    background-color: var(--input-background-color);

                    font-size: var(--input-font-size);
                    line-height: var(--input-line-height);
                    min-height: var(--input-height);
                    padding-inline: var(--input-spacing-x);
                    padding-block: var(--input-spacing-y);

                    outline: var(--input-border-color) solid var(--input-border-thickness);
                    outline-offset: calc(var(--input-border-thickness) * -1);

                    .MuiInputBase-input,
                    .MuiNativeSelect-select,
                    .MuiSelect-select {
                        color: ${theme.tokens.FuelColorTextPrimary};
                        -webkit-text-fill-color: ${theme.tokens.FuelColorTextPrimary};

                        &::placeholder {
                            -webkit-text-fill-color: ${theme.tokens.FuelColorTextTertiary};
                            color: ${theme.tokens.FuelColorTextTertiary};
                        }
                    }

                    .MuiInputBase-input {
                        flex-grow: 1;
                    }

                    &:has(.MuiNativeSelect-select),
                    &:has(.MuiSelect-select) {
                        cursor: pointer;
                        padding-right: 0rem;

                        .MuiNativeSelect-select,
                        .MuiSelect-select {
                            height: var(--input-height);
                            line-height: var(--input-height);
                            margin-block: calc(var(--input-spacing-y) * -1);
                            padding-right: calc(var(--input-spacing-x) + var(--input-icon-size));

                            ~ .MuiNativeSelect-icon,
                            ~ .MuiSelect-icon {
                                pointer-events: none;
                                right: var(--input-spacing-x);
                            }
                        }
                    }


                    .MuiInputAdornment-root {
                        height: var(--input-icon-box-size);
                        display: inline-flex;
                        align-items: center;
                    }

                    ${isSmallInput} {
                        --input-font-size: ${theme.tokens.FuelFontSizeSm};
                        --input-line-height: ${theme.tokens.FuelLineHeightSm};
                        --input-height: 32px;

                        .MuiInputAdornment-root {
                            margin-block: -2px;
                        }
                    }

                    ${isMediumInput} {
                        --input-font-size: ${theme.tokens.FuelFontSizeBase};
                        --input-line-height: ${theme.tokens.FuelLineHeightBase};
                        --input-height: 36px;
                    }

                    ${isLargeInput} {
                        --input-font-size: ${theme.tokens.FuelFontSizeBase};
                        --input-line-height: ${theme.tokens.FuelLineHeightBase};
                        --input-height: 40px;
                    }

                    /* VARIANTS ---- START */
                    &.MuiInput-root {
                        box-shadow: ${theme.tokens.FuelShadowMd};
                    }

                    &.MuiFilledInput-root {
                        --input-background-color: ${theme.tokens.FuelColorGray100};
                    }

                    &.MuiOutlinedInput-root {
                        --input-border-color: ${theme.tokens.FuelColorBorderDark};
                    }
                    /* VARIANTS ---- END */

                    &.MuiInputBase-fullWidth {
                        width: 100%;
                    }

                    &.Mui-focused {
                        --input-border-color: ${theme.tokens.FuelColorFocused};
                        --input-border-thickness: 2px;
                    }

                    &.Mui-error {
                        --input-border-color: ${theme.tokens.FuelColorAlertErrorBorder};
                    }

                    &.Mui-disabled {
                        opacity: 0.4;

                        .MuiInputBase-input,
                        .MuiNativeSelect-select,
                        .MuiSelect-select {
                            color: ${theme.tokens.FuelColorTextPrimary};
                            -webkit-text-fill-color: ${theme.tokens.FuelColorTextPrimary};

                            &::placeholder {
                                color: ${theme.tokens.FuelColorTextTertiary};
                                -webkit-text-fill-color: ${theme.tokens.FuelColorTextTertiary};
                            }
                        }
                    }
                }
            `,
            formControl: css `
                gap: 0.5rem;
            `
        }
    },
    MuiInput: {
        styleOverrides: {
            root: ({ theme }) => css `
                box-shadow: ${theme.tokens.FuelShadowMd};
            `,
            input: cssReset
        }
    },
    MuiFilledInput: {},
    MuiOutlinedInput: {
        defaultProps: {
            notched: false
        },
        styleOverrides: {
            root: ({ theme }) => css `
                outline-color: ${theme.tokens.FuelColorBorderDark};
            `,
            notchedOutline: css `
                border: none;
                display: none;
            `
        }
    }
};
